import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Search,
} from "lucide-react";
import { conflictChecksApi } from "../../services/api";
import ConflictCheckModal from "./ConflictCheckModal";

interface ConflictCheckBannerProps {
  clientId?: string;
  matterId?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

const ConflictCheckBanner: React.FC<ConflictCheckBannerProps> = ({
  clientId,
  matterId,
  clientName,
  clientEmail,
  clientPhone,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [checkId, setCheckId] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [resolvedBy, setResolvedBy] = useState<string | null>(null);
  const [waiverReason, setWaiverReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [runningAutoCheck, setRunningAutoCheck] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (clientId) {
        res = await conflictChecksApi.forClient(clientId);
      } else if (matterId) {
        res = await conflictChecksApi.forMatter(matterId);
      }
      if (res?.data) {
        const check = res.data;
        setStatus(check.status || null);
        setCheckId(check._id || null);
        setCheckedAt(check.resolvedAt || check.createdAt || null);
        setResolvedBy(check.resolvedBy?.name || null);
        setWaiverReason(check.waiverReason || null);
      } else {
        setStatus(null);
      }
    } catch (err: any) {
      // 404 means no check run yet
      if (err?.response?.status === 404) {
        setStatus(null);
      } else {
        console.error("Failed to fetch conflict check status:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, matterId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRunCheck = async () => {
    setRunningAutoCheck(true);
    try {
      if (clientId) {
        await conflictChecksApi.autoCheckClient(clientId);
      } else if (matterId) {
        await conflictChecksApi.autoCheckMatter(matterId);
      }
      await fetchStatus();
    } catch {
      // If auto-check fails, open the manual modal instead
      setShowModal(true);
    } finally {
      setRunningAutoCheck(false);
    }
  };

  const handleModalResolved = (newStatus: string) => {
    setStatus(newStatus);
    setShowModal(false);
    fetchStatus();
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        className="mb-4"
      >
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="text-sm text-slate-500">Checking conflict status...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-4"
      >
        <AnimatePresence mode="wait">
          {status === "clear" || status === "passed" ? (
            <motion.div
              key="clear"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200"
            >
              <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm font-medium text-emerald-800">
                Conflict check passed
                {checkedAt && (
                  <span className="font-normal text-emerald-600">
                    {" "}
                    — {new Date(checkedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </span>
            </motion.div>
          ) : status === "potential" ? (
            <motion.div
              key="potential"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-between px-4 py-3 rounded-xl bg-amber-50 border border-amber-200"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm font-medium text-amber-800">
                  Potential conflicts detected
                </span>
              </div>
              {checkId && (
                <button
                  onClick={() => navigate("/conflict-checks")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  View Details
                </button>
              )}
            </motion.div>
          ) : status === "confirmed" || status === "failed" ? (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200"
            >
              <ShieldAlert className="h-4 w-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-medium text-red-800">
                Conflict confirmed
                {checkedAt && (
                  <span className="font-normal text-red-600">
                    {" "}
                    — {new Date(checkedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </span>
            </motion.div>
          ) : status === "waived" ? (
            <motion.div
              key="waived"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200"
            >
              <ShieldCheck className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-800">
                <span className="font-medium">Conflict waived</span>
                {resolvedBy && <span className="font-normal"> by {resolvedBy}</span>}
                {waiverReason && <span className="font-normal"> — {waiverReason}</span>}
                {checkedAt && (
                  <span className="font-normal text-blue-600">
                    {" "}
                    — {new Date(checkedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="not_run"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 border border-slate-200"
            >
              <div className="flex items-center gap-2">
                <ShieldQuestion className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-500">
                  No conflict check has been run
                </span>
              </div>
              <button
                onClick={handleRunCheck}
                disabled={runningAutoCheck}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors text-white"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#146C43"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#0F5132"}
              >
                {runningAutoCheck ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Search className="h-3 w-3" />
                    Run Check
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Inline conflict check modal */}
      <ConflictCheckModal
        open={showModal}
        onClose={() => setShowModal(false)}
        searchTerms={[
          {
            ...(clientName ? { name: clientName } : {}),
            ...(clientEmail ? { email: clientEmail } : {}),
            ...(clientPhone ? { phone: clientPhone } : {}),
          },
        ].filter((t) => Object.keys(t).length > 0)}
        excludeClientId={clientId}
        onResolved={handleModalResolved}
      />
    </>
  );
};

export default ConflictCheckBanner;
